import { useState, useMemo } from "react";
import useFinanceiro from "../../hooks/useFinanceiro";
import { agruparPorMes } from "../../utils/agruparPorMes";
import ModalPagamento from "../../components/financeiro/ModalPagamento";
import { formatarMoeda } from "../../utils/formatarMoeda";
import { api } from "../../api/api";

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ReferenceDot
} from "recharts";

export default function Financeiro() {
  const { parcelas, pagarParcela } = useFinanceiro();

  const [selected, setSelected] = useState(null);
  const [mesAberto, setMesAberto] = useState(null);
  const [anoSelecionado, setAnoSelecionado] = useState(new Date().getFullYear());
  const [mesSelecionado, setMesSelecionado] = useState(null);
  const [actionType, setActionType] = useState(null);

  const toggleMes = (mes) => {
    setMesAberto(mesAberto === mes ? null : mes);
  };

  // 🔍 FILTRO PRINCIPAL
  const parcelasFiltradas = useMemo(() => {
    return parcelas.filter((p) => {
      const data = new Date(p.dataVencimento || p.dataPagamento);

      const matchAno = data.getFullYear() === Number(anoSelecionado);
      const matchMes = mesSelecionado
        ? data.getMonth() + 1 === Number(mesSelecionado)
        : true;

      return matchAno && matchMes;
    });
  }, [parcelas, anoSelecionado, mesSelecionado]);

  // 📦 AGRUPAMENTO
  const agrupado = useMemo(() => {
    return agruparPorMes(parcelasFiltradas);
  }, [parcelasFiltradas]);

  // 💰 KPI ANUAL
  const resumoAno = useMemo(() => {
    return parcelasFiltradas.reduce(
      (acc, p) => {
        const valor = Number(p.valor);

        acc.total += valor;

        if (p.status === "pago") acc.recebido += valor;
        else acc.pendente += valor;

        return acc;
      },
      { total: 0, recebido: 0, pendente: 0 }
    );
  }, [parcelasFiltradas]);

  const mediaMensal = resumoAno.total / 12;

  // 📊 ANO ANTERIOR
  const resumoAnoAnterior = useMemo(() => {
    const parcelasAnoAnterior = parcelas.filter((p) => {
      const data = new Date(p.dataVencimento || p.dataPagamento);
      return data.getFullYear() === Number(anoSelecionado) - 1;
    });

    return parcelasAnoAnterior.reduce(
      (acc, p) => {
        const valor = Number(p.valor);

        acc.total += valor;

        if (p.status === "pago") acc.recebido += valor;
        else acc.pendente += valor;

        return acc;
      },
      { total: 0, recebido: 0, pendente: 0 }
    );
  }, [parcelas, anoSelecionado]);

  // 📈 CRESCIMENTO
  const crescimentoReceita =
    resumoAnoAnterior.recebido === 0
      ? 100
      : ((resumoAno.recebido - resumoAnoAnterior.recebido) /
          resumoAnoAnterior.recebido) *
        100;

  // 📊 DADOS GRÁFICO
  const dadosGrafico = useMemo(() => {
    return Object.keys(agrupado).map((mes) => {
      const lista = agrupado[mes];

      const total = lista.reduce((a, p) => a + Number(p.valor), 0);
      const pago = lista
        .filter((p) => p.status === "pago")
        .reduce((a, p) => a + Number(p.valor), 0);

      const pendente = total - pago;

      return {
        mes,
        total,
        pago,
        pendente,
        media: total / (lista.length || 1)
      };
    });
  }, [agrupado]);

  // ⚠ MESES CRÍTICOS
  const mesesCriticos = useMemo(() => {
    return dadosGrafico.filter(
      (m) => m.total > 0 && m.pendente / m.total > 0.3
    );
  }, [dadosGrafico]);

  // 📂 AGRUPADO FILTRADO
  const agrupadoFiltrado = useMemo(() => {
    return Object.keys(agrupado)
      .filter((mes) => mes.includes(String(anoSelecionado)))
      .reduce((acc, mes) => {
        acc[mes] = agrupado[mes];
        return acc;
      }, {});
  }, [agrupado, anoSelecionado]);

  // 🏆 RANKING
  const ranking = useMemo(() => {
    const mapa = {};

    Object.values(agrupado).forEach((lista) => {
      lista.forEach((p) => {
        const id = p.clienteId?._id;
        const nome = p.clienteId?.nome || "Desconhecido";

        if (!mapa[id]) {
          mapa[id] = {
            cliente: nome,
            total: 0,
            pago: 0,
            pendente: 0
          };
        }

        const valor = Number(p.valor);

        mapa[id].total += valor;

        if (p.status === "pago") mapa[id].pago += valor;
        else mapa[id].pendente += valor;
      });
    });

    return Object.values(mapa)
      .map((c) => ({
        ...c,
        percentual: c.total ? c.pendente / c.total : 0
      }))
      .sort((a, b) => b.pendente - a.pendente);

  }, [agrupado]);

  const calcularResumo = (lista) => {
    const total = lista.reduce((acc, p) => acc + Number(p.valor), 0);
    const pago = lista
      .filter((p) => p.status === "pago")
      .reduce((acc, p) => acc + Number(p.valor), 0);

    return { total, pago, pendente: total - pago };
  };

  const getStatusMes = (pendente, total) => {
    const percentual = pendente / total;
    if (percentual === 0) return "ok";
    if (percentual < 0.3) return "medio";
    return "critico";
  };

  // 📥 BAIXAR PDF
  const baixarComprovante = async (parcela) => {
    setActionType("baixar")
    try {
      const id = parcela._id;

      const response = await api.get(
        `/parcelas/${id}/comprovante`,
        {
          responseType: "blob"
        }
      );

      const blob = new Blob(
        [response.data],
        { type: "application/pdf" }
      );

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `comprovante-${id}.pdf`;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error("Erro ao baixar comprovante:", error);

      if (error.response?.status === 401) {
        alert("Sua sessão expirou. Faça login novamente.");
      } else {
        alert("Não foi possível baixar o comprovante.");
      }
    } finally {
      setActionType(null);
    }
  };

  return (
    <div className="financeiro-page">
      <h1>GESTÃO FINANCEIRA</h1>
      <hr />

      {/* ALERTAS */}
      {mesesCriticos.map((m) => (
        <ReferenceDot key={m.mes} x={m.mes} y={m.total} r={6} fill="red" />
      ))}

      {/* FILTROS */}
      <div className="financeiro-filtros">

        <div className="financeiro-filtro-item">
          <label>Ano:</label>
          <select
            value={anoSelecionado}
            onChange={(e) => setAnoSelecionado(e.target.value)}
          >
            {[2023,2024,2025,2026,2027,2028,2029,2030].map((ano) => (
              <option key={ano} value={ano}>{ano}</option>
            ))}
          </select>
        </div>

        <div className="financeiro-filtro-item">
          <label>Mês:</label>
          <select
            value={mesSelecionado || ""}
            onChange={(e) => setMesSelecionado(e.target.value || null)}
          >
            <option value="">Todos</option>
            {["01","02","03","04","05","06","07","08","09","10","11","12"].map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>

      </div>

      {/* KPI */}
      <div className="kpi-grid">

        <div className="kpi-card">
          <h4>Total Geral</h4>
          <span>R$ {formatarMoeda(resumoAno.total.toFixed(2))}</span>
        </div>

        <div className="kpi-card">
          <h4>Recebido</h4>
          <span style={{ color: "#4b49aa" }}>
            R$ {formatarMoeda(resumoAno.recebido.toFixed(2))}
          </span>
        </div>

        <div className="kpi-card">
          <h4>Pendente</h4>
          <span style={{ color: "#ef4444" }}>
            R$ {formatarMoeda(resumoAno.pendente.toFixed(2))}
          </span>
        </div>

        <div className="kpi-card">
          <h4>Média Mensal</h4>
          <span>R$ {formatarMoeda(mediaMensal.toFixed(2))}</span>
        </div>

        <div className="kpi-card">
          <h4>Crescimento Receita</h4>
          <span style={{ color: crescimentoReceita >= 0 ? "#22c55e" : "#ef4444" }}>
            {crescimentoReceita.toFixed(1)}%
          </span>
        </div>

      </div>

      {/* GRID */}
      <div className="financeiro-grid">

        {/* ACCORDION */}
        <div>

          {Object.keys(agrupadoFiltrado).map((mes) => {
            const lista = agrupadoFiltrado[mes];
            const resumo = calcularResumo(lista);
            const aberto = mesAberto === mes;
            const status = getStatusMes(resumo.pendente, resumo.total);

            return (
              <div key={mes} className={`accordion-item ${status}`}>

                <div onClick={() => toggleMes(mes)} className="accordion-header">
                  <div>
                    <strong>{mes}</strong>

                    {status === "critico" && (
                      <span className="badge critico">Crítico</span>
                    )}

                    <div className="accordion-sub">
                      {lista.length} parcelas •
                      Pago: R$ {formatarMoeda(resumo.pago.toFixed(2))} •
                      Pendente: R$ {formatarMoeda(resumo.pendente.toFixed(2))}
                    </div>
                  </div>

                  <div className={`accordion-icon ${aberto ? "open" : ""}`}>
                    ▼
                  </div>
                </div>

                <div className={`accordion-body ${aberto ? "open" : ""}`}>

                  <table className="table">
                    <thead>
                      <tr>
                        <th>Cliente</th>
                        <th>Valor</th>
                        <th>Status</th>
                        <th>Ação</th>
                      </tr>
                    </thead>

                    <tbody>
                      {lista.map((p) => (
                        <tr key={p._id}>
                          <td>{p.clienteId?.nome}</td>
                          <td>
                            {p.numeroParcela || 1}/{p.totalParcelas || 1} - R$ {p.valor}
                          </td>
                          <td>{p.status}</td>
                          <td className="action-buttons">

                            {p.status === "pago" ? (
                              <>
                                <button disabled style={{ background: "#22c55e" }}>
                                  Pago ✓
                                </button>

                                <button
                                  onClick={() => baixarComprovante(p)}
                                  style={{ background: "#6366f1" }}
                                  disabled={actionType !== null}
                                >
                                  {actionType === "baixar" ? (
                                    <span className="spinner-loader"></span>
                                  ) : (
                                    "Baixar"
                                  )}
                                </button>
                              </>
                            ) : (
                              <button 
                                onClick={() => setSelected(p)}
                              >
                                Pagar
                              </button>
                            )}

                          </td>
                        </tr>
                      ))}
                    </tbody>

                  </table>

                </div>

              </div>
            );
          })}

        </div>

        {/* DASHBOARD */}
        <div className="dashboard-financeiro">

          <h3>Visão Geral Financeira</h3>

          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={dadosGrafico}>
              <XAxis dataKey="mes" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="pago" name="Recebido" fill="#4b49aa" />
              <Bar dataKey="pendente" name="Pendente" fill="#ef4444" />
            </BarChart>
          </ResponsiveContainer>

          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={dadosGrafico}>
              <XAxis dataKey="mes" />
              <YAxis />
              <Tooltip />
              <CartesianGrid stroke="#eee" />
              <Legend />

              <Line type="monotone" dataKey="pago" stroke="#4b49aa" />
              <Line type="monotone" dataKey="pendente" stroke="#ef4444" />
            </LineChart>
          </ResponsiveContainer>

        </div>

        {/* RANKING */}
        <div className="ranking-box">

          <h3>Ranking de Inadimplentes</h3>

          <table className="table">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Total</th>
                <th>Pago</th>
                <th>Pendente</th>
                <th>%</th>
              </tr>
            </thead>

            <tbody>
              {ranking.slice(0, 10).map((c, index) => (
                <tr key={index}>
                  <td><strong>{index + 1}º {c.cliente}</strong></td>
                  <td>R$ {formatarMoeda(c.total.toFixed(2))}</td>
                  <td style={{ color: "#22c55e" }}>R$ {formatarMoeda(c.pago.toFixed(2))}</td>
                  <td style={{ color: "#ef4444" }}>R$ {formatarMoeda(c.pendente.toFixed(2))}</td>
                  <td>{(c.percentual * 100).toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>

        </div>

      </div>

      {selected && (
        <ModalPagamento
          parcela={selected}
          onClose={() => setSelected(null)}
          onPagar={pagarParcela}
        />
      )}
    </div>
  );
}